import {bindAll} from '#core/utils/utils.js';
import {getCSSFromFontsArray} from '#core/utils/embedding-utils.js';
import {absoluteUrlToInternalResource} from '#core/utils/url-utils.js';
import {isLightColor} from '#core/utils/color-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {Localization} from '#core/localization.js';

import {BasicPalette} from '#core/application/modules/basic-palette.js';

export class GuidesContentGenerator {
    constructor(palette, excludedGuides = [], explicitTheme) {
        this.styles = this.getStyles(palette);
        this.excludedGuidesMap = excludedGuides.reduce((reducer, guideId) => ({...reducer, [guideId]: true}), {});
        this.theme = explicitTheme;

        if (this.theme === 'auto') {
            this.theme = isLightColor(BasicPalette.palette.DEFAULT_PAGE_BG, false) ? 'light' : 'dark';
        }

        bindAll(
            this,
            'getImagePath',
            'getAddingSelectionGuide',
            'getYourBetsGuide',
            'getBetslipGuide',
            'getCashOutGuide',
            'getInPlayGuide',
            'getYourAccountGuide',
            'getBetBuilderGuide',
            'getHomeGuide'
        );

        this.guidesMap = {
            addingSelection: this.getAddingSelectionGuide,
            yourBets: this.getYourBetsGuide,
            betslip: this.getBetslipGuide,
            cashout: this.getCashOutGuide,
            inplay: this.getInPlayGuide,
            yourAccount: this.getYourAccountGuide,
            betBuilder: this.getBetBuilderGuide,
            home: this.getHomeGuide,
        };
    }

    getStyles(PALETTE) {
        const {FONT} = PALETTE;

        return {
            container: 'padding: 8px 0px;',
            title: `font-size: 18px; font-weight: ${
                PALETTE.QSG_DIALOG_TITLE_TEXT.fontWeight || FONT.WEIGHT_BOLD
            }; color: ${PALETTE.QSG_DIALOG_TITLE_TEXT.color}; text-align: center; padding-bottom: 24px`,
            text: `font-family: ${FONT.FAMILY}; font-size: 14px; color: ${PALETTE.QSG_DIALOG_TEXT.color}`,
            image: 'width: 100%; height: auto; padding: 8px; box-sizing: border-box;',
        };
    }

    getImagePath(imageSource) {
        return `${absoluteUrlToInternalResource('images/quick-start-guide')}/${this.theme}/${imageSource}`;
    }

    getAddingSelectionGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('markets.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TEXT_2')}
                    </div>
                    <img src="${this.getImagePath('selections.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TEXT_3')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TEXT_4')}
                    </div>
                    <img src="${this.getImagePath('slip.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.ADDING_A_SELECTION.TEXT_5')}
                    </div>
                </div>
            `,
        };
    }

    getYourBetsGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_BETS.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_BETS.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_BETS.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('appbar.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_BETS.TEXT_2')}
                    </div>
                    <img src="${this.getImagePath('yourbets.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_BETS.TEXT_3')}
                    </div>
                </div>
            `,
        };
    }

    getBetslipGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_1')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_2')}
                    </div>
                    <img src="${this.getImagePath('slip-top.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_3')}
                    </div>
                    <img src="${this.getImagePath('slip-input.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_4')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_5')}
                    </div>
                    <img src="${this.getImagePath('slip-multi.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.THE_BETSLIP.TEXT_6')}
                    </div>
                </div>
            `,
        };
    }

    getCashOutGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.CASH_OUT.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.CASH_OUT.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.CASH_OUT.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('cashoutyourbet.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.CASH_OUT.TEXT_2')}
                    </div>
                    <img src="${this.getImagePath('confirmyourbet.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.CASH_OUT.TEXT_3')}
                    </div>
                </div>
            `,
        };
    }

    getInPlayGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('coupon.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TEXT_2')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TEXT_3')}
                    </div>
                    <img src="${this.getImagePath('event.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.INPLAY_BETTING.TEXT_4')}
                    </div>
                </div>
            `,
        };
    }

    getYourAccountGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_ACCOUNT.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_ACCOUNT.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_ACCOUNT.TEXT_1')}
                    </div>
                    <img src=${this.getImagePath('header.png')} style="${this.styles.image}"/>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.YOUR_ACCOUNT.TEXT_2')}
                    </div>
                    <img src="${this.getImagePath('account.png')}" style="${this.styles.image}">
                </div>
            `,
        };
    }

    getBetBuilderGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('betbuilder-example.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container} display: flex; align-items: center">
                        <img src="${this.getImagePath('system-betbuilder.png')}" style="width: 48px; height: 48px">
                        <span style="padding: 8px 0 8px 16px">
                            ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TEXT_2')}
                        </span>
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TEXT_3')}
                    </div>
                    <img src="${this.getImagePath('betbuilder.png')}" style="${this.styles.image}">
                    <div style="${this.styles.container}">
                        <i>${Localization.getTranslation('$DBX.QUICK_START_GUIDE.BET_BUILDER.TEXT_4')}</i>
                    </div>
                </div>
            `,
        };
    }

    getHomeGuide() {
        return {
            name: Localization.getTranslation('$DBX.QUICK_START_GUIDE.HOME.TITLE'),
            content: `
                <div style="${this.styles.text}">
                    <div style="${this.styles.title}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.HOME.TITLE')}
                    </div>
                    <div style="${this.styles.container}">
                        ${Localization.getTranslation('$DBX.QUICK_START_GUIDE.HOME.TEXT_1')}
                    </div>
                    <img src="${this.getImagePath('home.png')}" style="${this.styles.image}"/>
                </div>
            `,
        };
    }

    generate() {
        const guidesIds = Object.keys(this.guidesMap);
        const fontsStylesCSS = `<style>${getCSSFromFontsArray(ApplicationConfig.configuration.fonts)}</style>`;

        return {
            menu: {
                title: `${Localization.getTranslation('$DBX.QUICK_START_GUIDE.HI_THERE.TITLE')}`,
                description: `${Localization.getTranslation('$DBX.QUICK_START_GUIDE.HI_THERE.TEXT')}`,
            },
            guides: guidesIds
                .filter(guideId => !this.excludedGuidesMap[guideId])
                .map(guideId => {
                    const guideGenerator = this.guidesMap[guideId];

                    const generatedGuide = guideGenerator();

                    return {
                        ...generatedGuide,
                        id: guideId,
                        content: `${fontsStylesCSS}${generatedGuide.content}`,
                    };
                }),
        };
    }
}
